import { useCallback, useRef } from 'react'

import { useApolloClient, useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import { CollaborationNoteReactionsFields } from './fragments/CollaborationNoteReactionsFields'
import { hasReactedWith, setNoteReaction } from './notesCache'
import { CollaborationNoteWithActionsFields } from './notesQueries'
import type {
  CollaborationNote,
  NoteReaction,
  NoteReactionAccount,
} from './notesQueries'

export const ReactToCollaborationNoteMutation = graphql(
  `
    mutation ReactToCollaborationNote($input: ReactToCollaborationNoteInput!) {
      reactToCollaborationNote(input: $input) {
        __typename
        ... on ReactToCollaborationNoteSuccess {
          note {
            ...CollaborationNoteWithActionsFields
          }
        }
        ... on InvalidInputError {
          message
        }
        ... on LimitReachedError {
          message
        }
      }
    }
  `,
  [CollaborationNoteWithActionsFields],
)

/**
 * Only a failure a retry could fix asks the account to try again: the server
 * rejects the same emoji at the same limit every time.
 */
function reactionFailureMessage(typename: string | undefined): string {
  switch (typename) {
    case 'LimitReachedError':
      return 'This note has reached its reaction limit.'
    case 'InvalidInputError':
      return 'That emoji cannot be used as a reaction.'
    default:
      return 'Could not update the reaction. Please try again.'
  }
}

/** The cache normalizes reaction accounts, so each object needs its type. */
function withTypenames(reactions: readonly NoteReaction[]): Array<
  NoteReaction & {
    __typename: 'CollaborationNoteReaction'
    accounts: Array<NoteReactionAccount & { __typename: 'Author' }>
  }
> {
  return reactions.map((reaction) => ({
    ...reaction,
    __typename: 'CollaborationNoteReaction',
    accounts: reaction.accounts.map((account) => ({
      ...account,
      __typename: 'Author',
    })),
  }))
}

let optimisticLayerCount = 0

/**
 * Toggles the current account's reaction. Other feeds receive it over the
 * Notes subscription.
 *
 * Each tap shows at once through its own Apollo optimistic layer, which
 * writes only the Note's reactions. A refetch or live update that lands while
 * the request is pending writes beneath the layer, so it cannot replace the
 * guess; what it changed shows once the layer goes. The layer goes when the
 * request settles: the server's Note then shows, or on failure the guess
 * simply disappears. Apollo re-runs the layers above it, so the taps still
 * pending stay laid over the latest Note.
 *
 * A second tap reads the first tap's guess, so tapping an emoji twice queues
 * its undo. Changes to one Note go to the server one at a time, so a response
 * is never older than the one before it. That is also why each tap records
 * its layer on the cache itself: a queued request shows its guess before
 * `mutate` is called for it.
 */
export function useReactToNote(options?: {
  /** Called when a tap adds the account's reaction, not when it removes one. */
  onAdd?: (emoji: string) => void
}): {
  toggle: (note: CollaborationNote, emoji: string) => Promise<void>
} {
  const { account } = useAccount()
  const client = useApolloClient()
  const [mutate] = useMutation(ReactToCollaborationNoteMutation, {
    errorPolicy: 'all',
  })
  const tailsRef = useRef<Map<string, Promise<void>>>(new Map())
  const onAdd = options?.onAdd

  const toggle = useCallback(
    async (note: CollaborationNote, emoji: string): Promise<void> => {
      if (!account || !note.allowedActions.includes('reactToNote')) return
      const { cache } = client
      const me = {
        id: account.id,
        name: account.name ?? null,
        email: account.email,
      }
      const id = cache.identify({
        __typename: 'CollaborationNote',
        id: note.id,
      })
      const current = cache.readFragment(
        { id, fragment: CollaborationNoteReactionsFields },
        true,
      )
      const reacted = !hasReactedWith(
        current?.reactions ?? note.reactions,
        emoji,
      )
      if (reacted) onAdd?.(emoji)

      const layerId = `reactToNote:${note.id}:${++optimisticLayerCount}`
      cache.recordOptimisticTransaction((layer) => {
        // Reads the Note beneath this layer: the server's latest Note with
        // any earlier taps still pending laid over it.
        const beneath = layer.readFragment({
          id,
          fragment: CollaborationNoteReactionsFields,
        })
        if (!beneath) return
        layer.writeFragment({
          id,
          fragment: CollaborationNoteReactionsFields,
          data: {
            ...beneath,
            reactions: withTypenames(
              setNoteReaction(beneath.reactions, emoji, me, reacted),
            ),
          },
        })
      }, layerId)

      const send = async (): Promise<void> => {
        try {
          const response = await mutate({
            variables: {
              input: {
                noteId: note.id,
                emoji,
                action: reacted ? 'add' : 'remove',
              },
            },
          })
          const payload = response.data?.reactToCollaborationNote
          if (payload?.__typename === 'ReactToCollaborationNoteSuccess') return
          toast.error(reactionFailureMessage(payload?.__typename))
        } catch {
          toast.error(reactionFailureMessage(undefined))
        } finally {
          cache.removeOptimistic(layerId)
        }
      }

      const tails = tailsRef.current
      const tail = (tails.get(note.id) ?? Promise.resolve()).then(send)
      tails.set(note.id, tail)
      await tail
      if (tails.get(note.id) === tail) tails.delete(note.id)
    },
    [account, client, mutate, onAdd],
  )

  return { toggle }
}
